import { useState } from "react";
import { useEmployeeTasks, useHrInvalidate } from "@/hooks/use-hr-queries";
import { hrEmployeeApi } from "@/lib/api/hr";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, Page, Panel, QueryGate, StatusBadge } from "@/components/shell/page";

export default function EmployeeTasksPage() {
  const { data, isLoading, error } = useEmployeeTasks();
  const invalidate = useHrInvalidate();
  const [extraTitle, setExtraTitle] = useState("");
  const [extraDesc, setExtraDesc] = useState("");
  const [notes, setNotes] = useState("");
  const [blockers, setBlockers] = useState("");

  return (
    <QueryGate loading={isLoading} error={error || !data ? "Could not load tasks." : null}>
      {data && (
        <Page
          title="Tasks"
          description="Mark today’s work, add anything extra, and send the daily report."
        >
          <Panel title="Assigned today">
            {(data.assignments ?? []).length === 0 && (
              <EmptyState title="Nothing assigned" body="When a task is assigned for today, it shows up here." />
            )}
            <ul className="divide-y">
              {(data.assignments ?? []).map((task: any) => (
                <li key={task.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                  <button
                    type="button"
                    disabled={task.status === "completed"}
                    aria-pressed={task.status === "completed"}
                    className="mt-0.5 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-lg border bg-card text-primary hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:opacity-70"
                    onClick={async () => {
                      await hrEmployeeApi.completeTask(task.id);
                      invalidate();
                    }}
                  >
                    <span className="sr-only">
                      {task.status === "completed" ? "Completed" : "Mark completed"}
                    </span>
                    <span
                      className={`h-4 w-4 rounded border ${task.status === "completed" ? "border-primary bg-primary" : "border-input"}`}
                      aria-hidden="true"
                    />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{task.title}</p>
                      <StatusBadge status={task.status} />
                    </div>
                    {task.description && (
                      <p className="mt-1 text-sm text-muted-foreground">{task.description}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </Panel>

          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Additional work" description="Work you did that was not on the list.">
              <ul className="mb-4 space-y-2">
                {(data.additional ?? []).map((item: any) => (
                  <li key={item.id} className="rounded-lg bg-muted px-3 py-2 text-sm">
                    {item.title}
                  </li>
                ))}
              </ul>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="extra-title">Title</Label>
                  <Input
                    id="extra-title"
                    placeholder="What else did you complete?"
                    value={extraTitle}
                    onChange={(e) => setExtraTitle(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="extra-desc">Details</Label>
                  <Textarea id="extra-desc" value={extraDesc} onChange={(e) => setExtraDesc(e.target.value)} />
                </div>
                <Button
                  onClick={async () => {
                    if (!extraTitle.trim()) return;
                    await hrEmployeeApi.additionalWork({ title: extraTitle, description: extraDesc });
                    setExtraTitle("");
                    setExtraDesc("");
                    invalidate();
                  }}
                >
                  Add work
                </Button>
              </div>
            </Panel>

            <Panel title="Daily report" description="A short note for your employer.">
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="blockers">Blockers</Label>
                  <Textarea id="blockers" value={blockers} onChange={(e) => setBlockers(e.target.value)} />
                </div>
                <Button
                  onClick={async () => {
                    await hrEmployeeApi.submitReport({ notes, blockers });
                    invalidate();
                  }}
                >
                  Submit report
                </Button>
              </div>
            </Panel>
          </div>
        </Page>
      )}
    </QueryGate>
  );
}
